import {
  Badge,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { ReactComponent as SortableColumn } from "../../../shared/assets/sort_icons/sortable-column.svg";
import { ReactComponent as SortIcon } from "../../../shared/assets/sort_icons/sort-icon.svg";
import { ReactComponent as DownloadIcon } from "../../../shared/assets/general/download.svg";
import store, { RootState } from "../../../store/store";
import {
  DASHBOARD_CONSTANTS,
  DATE_FILTERS,
  DEVICE_TYPES,
  PAGES_URL,
  STATUS,
} from "../../../shared/constants/constants";
import {
  setDateSpanRedux,
  setFilters,
  setPagination,
  setSelectedSort,
  setSelectedTab,
  setStoreSearchText,
} from "../../../store/reducers/dashboard";
import CustomSpinner from "../../../shared/components/customSpinner/customSpinner";
import { getGRNColor, getOrderColor } from "../../../shared/utils/colorUtils";
import CustomBadge from "../../../shared/components/customBadge/customBadge";
import { Search, UploadFile } from "@mui/icons-material";
import { uploadFileWrapper } from "../../../shared/utils/fileUtils";
import { useSnackbar } from "notistack";
import NavigateBackButton from "../../../shared/components/customNavigateButton/customNavigateButton";
import {
  acknowledgeGRNApi,
  getAllGrn,
  getGrnPdf,
} from "../../../store/api/grn";

const GRNTable = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const storeGRNState = useSelector((state: RootState) => state.grn);
  const storePartyState = useSelector((state: RootState) => state.party);
  const storeStatusState = useSelector(
    (state: RootState) => state.dashboard.filters,
  );
  const storePageState = useSelector(
    (state: RootState) => state.dashboard.currentPage,
  );
  const storeRowsPerPageState = useSelector(
    (state: RootState) => state.dashboard.pageSize,
  );
  const storeSortState = {
    type: useSelector((state: RootState) => state.dashboard.sortField),
    value: useSelector((state: RootState) => state.dashboard.sortOrder),
  };
  const storeTabState = useSelector(
    (state: RootState) => state.dashboard.selectedTab,
  );
  const storeSearchTextState = useSelector(
    (state: RootState) => state.dashboard.searchText,
  );
  const storeDateState = useSelector(
    (state: RootState) => state.dashboard.dateSpan,
  );
  const theme = useTheme();
  const isLessThanMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLessThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [status, setStatus] = useState(
    DASHBOARD_CONSTANTS.STATUS_FILTER.DELIVERY_CHALLAN.ALL.id,
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<any>({});
  const [pageCount, setPageCount] = useState(0);
  const tab = DASHBOARD_CONSTANTS.TABS.DELIVERY_CHALLANS;
  const [totalRows, setTotalRows] = useState(0);
  const statusList = DASHBOARD_CONSTANTS.STATUS_FILTER.DELIVERY_CHALLAN;
  const [dateSpan, setDateSpan] = useState<any>({
    // Show every Created GRN regardless of when it was created - there's no
    // date filter control on this page, so ALL (unbounded) is the only
    // range that satisfies that.
    type: DATE_FILTERS.ALL.value,
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()),
  });
  const [sort, setSort] = useState<{
    type: string;
    value: "asc" | "desc";
  }>({
    type: "createdDate",
    value: "desc",
  });
  const [mount, setMount] = useState(false);
  const [isLoading, setIsLoading] = useState<string>("");
  const [openGrn, setOpenGrn] = useState<string>("");

  // Search & Debounce States
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // Debounce Effect: Updates debounced text after 500ms of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  useEffect(() => {
    if (mount) {
      //updating redux states for preserving page and sorts
      dispatch(setPagination({ currentPage: page, pageSize: rowsPerPage }));
      dispatch(setFilters(status));
      dispatch(setSelectedTab(tab));
      dispatch(
        setSelectedSort({
          sortField: sort.type,
          sortOrder: sort.value === "asc" ? "asc" : "desc",
        }),
      );
      dispatch(setStoreSearchText(debouncedSearchText)); // Update store with debounced text
      dispatch(
        setDateSpanRedux({
          type: dateSpan.type,
          startDate: dateSpan.startDate.toString(),
          endDate: dateSpan.endDate.toString(),
        }),
      );
      fetchRequests(debouncedSearchText); // Fetch with debounced text
    }
  }, [
    page,
    rowsPerPage,
    status,
    tab,
    sort,
    statusList,
    debouncedSearchText, // Replaced searchText with debouncedSearchText
    dateSpan,
    storePartyState.currentOrg,
    mount,
  ]);

  useEffect(() => {
    // Set the initial local state from Redux values on mount
    if (storeTabState === DASHBOARD_CONSTANTS.TABS.DELIVERY_CHALLANS) {
      setStatus(storeStatusState || "");
      setPage(storePageState || 1);
      setRowsPerPage(storeRowsPerPageState || 10);
      setSort({
        type: storeSortState.type || "createdDate",
        value: storeSortState.value || "desc",
      });
      setSearchText(storeSearchTextState || "");
      setDebouncedSearchText(storeSearchTextState || ""); // Initialize debounced state as well
      setDateSpan({
        type: storeDateState.type || DATE_FILTERS.ALL.value,
        startDate: dayjs(storeDateState.startDate) || dayjs(new Date()),
        endDate: dayjs(storeDateState.endDate) || dayjs(new Date()),
      });
    }
    setMount(true);
  }, []);

  const handleSearch = (searchString: string) => {
    // Only update the immediate text state. The useEffect will handle the API trigger.
    setSearchText(searchString);
  };

  // This list only ever shows Created (not yet acknowledged) GRNs from
  // 1-Aug-2026 IST onward. Both the status and date filters are now sent to
  // the backend (/order/grn/getAll supports both) along with the actual
  // requested page/pageSize, instead of fetching a large buffer and
  // filtering/paginating client-side.
  // Sent as a plain YYYY-MM-DD string; the backend anchors it to IST
  // midnight via getISTStartOfDay, so this is timezone-safe regardless of
  // the client's local timezone. Keep this in sync with the same cutoff
  // used in invoiceTable.tsx for the invoice list.
  const AUG_2026_CUTOFF = "2026-08-01";

  const fetchRequests = (searchString: any) => {
    let inputSearchText = debouncedSearchText;
    if (searchString && searchString != "") {
      inputSearchText = searchString;
    }
    store
      .dispatch(
        getAllGrn({
          status: STATUS.GRN.GRN_CREATED.id,
          text: inputSearchText,
          page: page,
          pageSize: rowsPerPage,
          sort: sort.type + ":" + sort.value,
          startDate: AUG_2026_CUTOFF,
          endDate: "",
        }),
      )
      .unwrap()
      .then((resp: any) => {
        setData({ grns: resp?.data?.grns?.grns ?? [] });
        const totalRecords = resp?.data?.grns?.totalRecords ?? 0;
        setTotalRows(totalRecords);
        setPageCount(Math.ceil(totalRecords / rowsPerPage));
      })
      .catch((err) => {
        enqueueSnackbar(err);
      });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleSort = (column: string) => {
    setPage(1);

    if (sort.type === column) {
      setSort({
        type: column,
        value: sort.value === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({
        type: column,
        value: "asc",
      });
    }
  };

  const downloadGRNPdf = (
    orderId: string,
    orderCode: string,
    goodsReqChallanCode: string,
  ) => {
    const reqBody = {
      orderId: orderId,
      orderUniqueCode: orderCode,
      goodsReqChallanCode: goodsReqChallanCode,
    };
    store
      .dispatch(getGrnPdf(reqBody))
      .unwrap()
      .catch(() => {
        enqueueSnackbar("Cannot generate GRN Pdf", { variant: "error" });
      });
  };

  const handleChangeDateType = (event: SelectChangeEvent) => {
    setPage(1);
    setDateSpan({
      ...dateSpan,
      type: event.target.value,
    });
  };

  const uploadDocument = async (file: any, type: string, grn: any) => {
    try {
      setIsLoading(grn?._id + "-" + "DocUpload");
      await uploadFileWrapper(file, type, grn._id);
      enqueueSnackbar(
        `${file[0].name} Uploaded Successfully for GRN: ${grn?.goodsReqChallanCode}`,
        { variant: "success" },
      );
      await store.dispatch(
        acknowledgeGRNApi({
          grnId: grn?._id,
          goodsReqChallanCode: grn?.goodsReqChallanCode,
          acknowledgementStatus: STATUS.GRN.GRN_DELIVERED.id,
        }),
      );
      fetchRequests(debouncedSearchText);
    } catch (error) {
      if (error instanceof AggregateError) {
        // Handle multiple errors
        console.error(`Aggregate Error caught: ${error.message}`);

        error.errors.forEach((subError) => {
          enqueueSnackbar(`${subError.message}`, { variant: "error" });
        });
      } else if (error instanceof Error) {
        // Handle standard errors (TypeError, ReferenceError, custom errors, etc.)
        console.log(error);
        enqueueSnackbar(`${error.message}`, { variant: "error" });
      }
    } finally {
      setIsLoading("");
    }
  };

  interface Column {
    id: string;
    label: string;
    width?: string;
    align?: "right" | "center" | "left";
    enableSort?: boolean;
    format?: (value: number) => string;
  }

  const grnItemColumns: Column[] = [
    {
      id: "orderId",
      label: "Order ID",
      width: "12%",
      enableSort: true,
      align: "center",
    },
    {
      id: "totalItems",
      label: "Total Items",
      width: "18%",
      align: "center",
    },
    {
      id: "grnId",
      label: "GRN ID",
      width: "21%",
      align: "center",
    },
    {
      id: "status",
      label: "Status",
      width: "15%",
      enableSort: true,
      align: "center",
    },
    {
      id: "createdDate",
      label: "Created Date",
      width: "15%",
      enableSort: true,
      align: "center",
    },
    {
      id: "actionsId",
      label: "Actions",
      width: "19%",
      enableSort: false,
      align: "center",
    },
  ];

  const grnTableRows = useMemo(() => {
    switch (storeGRNState.loading) {
      case true:
      case undefined:
        return (
          <TableRow sx={{ height: "80%" }}>
            <TableCell
              align="center"
              sx={{ borderBottom: "none" }}
              colSpan={grnItemColumns.length}
            >
              <CustomSpinner size={"2rem"} />
            </TableCell>
          </TableRow>
        );
      case false:
        return data?.grns?.length ? (
          data.grns.map((grn: any) => {
            const totQuant = grn?.items?.reduce(
              (total: number, item: any) => total + Number(item?.quantity),
              0,
            );
            const statusId = grn?.statusId; // Use status from statusMap or default to grn status
            const className = getGRNColor(statusId);
            if (isLessThanMd) {
              return (
                <Stack width={"100%"} key={grn?._id}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    onClick={() => {
                      openGrn === grn?._id
                        ? setOpenGrn("")
                        : setOpenGrn(grn?._id);
                    }}
                  >
                    <Stack direction={"row"} spacing={1} alignItems={"center"}>
                      <Typography>
                        {grn?.goodsReqChallanCode?.split("/")[3] ||
                          grn?.goodsReqChallanCode}
                      </Typography>
                      <CustomBadge
                        content={
                          STATUS.GRN[statusId as keyof typeof STATUS.GRN].name
                        }
                        className={className}
                        sx={{ minWidth: "unset" }}
                      />
                    </Stack>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading === grn?._id + "-" + "DocUpload" ? (
                        <CustomSpinner />
                      ) : (
                        <IconButton
                          sx={{
                            borderRadius: 0,
                            mr: 1,
                          }}
                          size="large"
                        >
                          <UploadFile height={"20px"} width={"20px"} />
                          <Input
                            type="file"
                            id="signedFileUpload"
                            onChange={(event) => {
                              if (event?.target instanceof HTMLInputElement) {
                                uploadDocument(
                                  event?.target?.files,
                                  "signedDeliveryChallan",
                                  grn,
                                );
                              }
                            }}
                            sx={{
                              width: 1,
                              height: 1,
                              opacity: 0,
                              border: "none",
                              position: "absolute",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        sx={{
                          borderRadius: 0,
                          mr: 1,
                        }}
                        onClick={() =>
                          downloadGRNPdf(
                            grn?.orderId?._id,
                            grn?.orderId?.orderCode,
                            grn?.goodsReqChallanCode,
                          )
                        }
                        size="large"
                      >
                        <DownloadIcon height={"20px"} width={"20px"} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Collapse in={openGrn === grn?._id}>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Status: </Typography>
                      <Typography>
                        {STATUS.GRN[statusId as keyof typeof STATUS.GRN].name}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Created Date: </Typography>
                      <Typography>
                        {dayjs(grn.createdDate).format("DD/MM/YYYY")}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Order ID: </Typography>
                      <Typography>
                        {grn?.orderId?.orderCode?.split("/")[5] ||
                          grn?.orderId?.orderCode}
                      </Typography>
                    </Stack>
                  </Collapse>
                </Stack>
              );
            } else {
              return (
                <TableRow key={grn._id}>
                  <TableCell align={grnItemColumns[5].align}>
                    <Typography>
                      {grn?.orderId?.orderCode?.split("/")[5] ||
                        grn?.orderId?.orderCode}
                    </Typography>
                  </TableCell>
                  <TableCell align={grnItemColumns[5].align}>
                    {parseFloat(totQuant).toFixed(2)}
                  </TableCell>
                  <TableCell align={grnItemColumns[5].align}>
                    <Typography>
                      {grn?.goodsReqChallanCode?.split("/")[3] ||
                        grn?.goodsReqChallanCode}
                    </Typography>
                  </TableCell>
                  <TableCell align={grnItemColumns[5].align}>
                    <CustomBadge
                      content={
                        STATUS.GRN[statusId as keyof typeof STATUS.GRN].name
                      }
                      className={className}
                    />
                  </TableCell>
                  <TableCell align={grnItemColumns[5].align}>
                    {dayjs(grn?.createdDate).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell align={grnItemColumns[5].align}>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading === grn?._id + "-" + "DocUpload" ? (
                        <CustomSpinner />
                      ) : (
                        <IconButton
                          sx={{
                            borderRadius: 0,
                            mr: 1,
                          }}
                          size="large"
                        >
                          <UploadFile height={"20px"} width={"20px"} />
                          <Input
                            type="file"
                            id="signedFileUpload"
                            onChange={(event) => {
                              if (event?.target instanceof HTMLInputElement) {
                                uploadDocument(
                                  event?.target?.files,
                                  "signedDeliveryChallan",
                                  grn,
                                );
                              }
                            }}
                            sx={{
                              width: 1,
                              height: 1,
                              opacity: 0,
                              border: "none",
                              position: "absolute",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        sx={{
                          borderRadius: 0,
                          mr: 1,
                        }}
                        onClick={() =>
                          downloadGRNPdf(
                            grn?.orderId?._id,
                            grn?.orderId?.orderCode,
                            grn?.goodsReqChallanCode,
                          )
                        }
                        size="large"
                      >
                        <DownloadIcon height={"20px"} width={"20px"} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            }
          })
        ) : (
          <TableRow>
            <TableCell
              sx={{ borderBottom: "none" }}
              colSpan={grnItemColumns.length}
            >
              No Data
            </TableCell>
          </TableRow>
        );
    }
  }, [
    grnItemColumns.length,
    data,
    storeGRNState.loading,
    isLoading,
    isLessThanMd,
    openGrn,
  ]);

  return (
    <Stack width={"100%"} p={2} boxSizing="border-box">
      <NavigateBackButton
        label={"Back to Home"}
        navigateLink={PAGES_URL.HOME}
      />

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        justifyContent="space-between"
        alignItems={{
          sm: "flex-start",
          xs: "center",
        }}
        spacing={2}
        my={1}
      >
        <Typography fontSize={26} fontWeight={600}>
          Delivery Challans List
        </Typography>
        <FormControl sx={{ m: 1 }} variant="standard" fullWidth={isLessThanSm}>
          <InputLabel htmlFor={`search-input`}>Search</InputLabel>
          <Input
            id={`search-input`}
            value={searchText} // Controlled input to ensure text syncs on initial mount
            startAdornment={
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            }
            onChange={(e) => handleSearch(e.target.value)}
          />
        </FormControl>
      </Stack>

      {!isLessThanMd && (
        <TableContainer>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {grnItemColumns.map((grnItemColumn) => (
                  <TableCell
                    align={grnItemColumn.align}
                    key={grnItemColumn.id}
                    sx={{
                      width: grnItemColumn.width,
                    }}
                  >
                    {grnItemColumn.enableSort && (
                      <TableSortLabel
                        className={
                          sort.type === grnItemColumn.id &&
                          sort.value === "desc"
                            ? "sortColumn inverted"
                            : "sortColumn"
                        }
                        active={true}
                        direction={sort.value}
                        hideSortIcon={true}
                        onClick={() => handleSort(grnItemColumn.id)}
                        IconComponent={() => {
                          if (sort.type !== grnItemColumn.id) {
                            return <SortableColumn style={{ marginLeft: 2 }} />;
                          } else {
                            return (
                              <SortIcon
                                style={{
                                  transform:
                                    sort.value === "desc"
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                  marginLeft: 2,
                                }}
                              />
                            );
                          }
                        }}
                      >
                        <p>{grnItemColumn.label}</p>
                      </TableSortLabel>
                    )}

                    {!grnItemColumn.enableSort && grnItemColumn.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{grnTableRows}</TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={grnItemColumns.length}>
                  <Stack
                    direction={"row"}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography>
                      Showing data{" "}
                      {totalRows ? (page - 1) * rowsPerPage + 1 : totalRows} to{" "}
                      {(page - 1) * rowsPerPage + rowsPerPage < totalRows
                        ? (page - 1) * rowsPerPage + rowsPerPage
                        : totalRows}{" "}
                      of {totalRows} entries
                    </Typography>
                    <Stack
                      direction={"row"}
                      spacing={1}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handleChangePage}
                        dir={""}
                      />
                      <Typography>Result Per Page</Typography>
                      <Select
                        labelId="row_count"
                        size="small"
                        id="RowCountField"
                        sx={{
                          ".MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          paddingRight: "0px",
                        }}
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      >
                        {DASHBOARD_CONSTANTS.PAGE_SIZES.map((key) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
      {isLessThanMd && (
        <Stack spacing={1} divider={<Divider variant="fullWidth" />}>
          {grnTableRows}
          <Pagination
            count={pageCount}
            page={page}
            onChange={handleChangePage}
            dir={""}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default GRNTable;
